import type { SceneHandle } from "../experiment/types.js";
import { mountResource, type ResourceScope } from "./lifecycle.js";

export type ThreeModule = typeof import("three");

export function cssColour(name: string, fallback: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

export function showFallback(stage: HTMLElement, message: string): void {
  const paragraph = document.createElement("p");
  paragraph.className = "verlet-scene__fallback";
  paragraph.textContent = message;
  stage.replaceChildren(paragraph);
}

export async function openRenderer(
  stage: HTMLElement, scope: ResourceScope,
  options: { readonly logarithmicDepthBuffer?: boolean } = {},
): Promise<{ three: ThreeModule; renderer: InstanceType<ThreeModule["WebGLRenderer"]> } | undefined> {
  const three = await import("three");
  if (scope.disposed) return undefined;
  const renderer = new three.WebGLRenderer({ antialias: true, alpha: true, ...options });
  scope.own(() => { renderer.dispose(); renderer.domElement.remove(); });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  stage.replaceChildren(renderer.domElement);
  return { three, renderer };
}

export function mountScene(
  root: ParentNode, selector: string, unavailable: string,
  attach: (scope: ResourceScope) => Promise<{ readonly redraw: () => void } | undefined>,
): SceneHandle {
  const lifecycle = mountResource(attach, (error) => {
    console.error(error);
    const stage = root.querySelector<HTMLElement>(selector);
    if (stage) showFallback(stage, unavailable);
  });
  const redraw = (): void => lifecycle.command("redraw", (api) => api.redraw());
  redraw();
  return { redraw, detach: lifecycle.detach };
}

export function observeScene(
  scope: ResourceScope, stage: HTMLElement, resize: () => void, theme: () => void,
): void {
  const observer = new ResizeObserver(resize);
  scope.own(() => observer.disconnect());
  observer.observe(stage);
  document.addEventListener("verlet-theme-change", theme);
  scope.own(() => document.removeEventListener("verlet-theme-change", theme));
}

/** Un matériau partagé par plusieurs mailles n'est libéré qu'une fois. */
export function dispose(group: { traverse: (callback: (object: unknown) => void) => void }): void {
  const resources = new Set<{ dispose: () => void }>();
  group.traverse((object) => {
    const candidate = object as { geometry?: { dispose: () => void }; material?: { dispose: () => void } | { dispose: () => void }[] };
    if (candidate.geometry) resources.add(candidate.geometry);
    if (Array.isArray(candidate.material)) candidate.material.forEach((material) => resources.add(material));
    else if (candidate.material) resources.add(candidate.material);
  });
  resources.forEach((resource) => resource.dispose());
}
